/** Contact details shown when a public form can't be sent. */
export const CONTACT_FALLBACK =
    "You can also call 778-918-4169 or email leanne.robb@westcoasthome.ca.";

/**
 * Guide request answered with 429: the API allows one guide of each type per
 * address per day (and rate-limits repeated submissions), so the likeliest
 * reason is that the guide is already in the visitor's inbox.
 */
export const GUIDE_TOO_MANY_MESSAGE = `We've already sent this guide to that address, or there have been too many requests. Please check your inbox and spam folder, or try again tomorrow. ${CONTACT_FALLBACK}`;

type FastApiDetail = string | { msg?: string }[] | undefined;

async function readDetail(res: Response): Promise<FastApiDetail> {
    try {
        const body = await res.json();
        return body?.detail;
    } catch {
        return undefined;
    }
}

function firstMessage(detail: FastApiDetail): string | undefined {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && typeof detail[0]?.msg === "string") {
        // FastAPI prefixes validator messages with "Value error, ".
        return detail[0].msg.replace(/^Value error,\s*/i, "");
    }
    return undefined;
}

/**
 * Builds the visitor-facing message for a failed public form request.
 * Never includes or logs submitted personal data.
 */
export async function messageForFailedResponse(res: Response): Promise<string> {
    if (res.status === 429) {
        return "Too many submissions. Please try again later.";
    }
    const message = firstMessage(await readDetail(res))?.trim();
    const fallback = res.status === 422
        ? "Please check your details and try again."
        : "Something went wrong and we couldn't send your request.";
    const text = message || fallback;
    // End the API's sentence before the contact fallback.
    return `${/[.!?]$/.test(text) ? text : `${text}.`} ${CONTACT_FALLBACK}`;
}

/** Message for a request that never got a response (network failure). */
export const NETWORK_ERROR_MESSAGE = `We couldn't reach the server. Please check your connection and try again. ${CONTACT_FALLBACK}`;
